import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';

import Icon from '@commons/components/Icon';
import Flex from '@commons/containers/Flex';
import { type Alternates, LOCALES } from '@commons/i18n';

import Text from '../Text';
import styles from './index.module.scss';

// Type definition for LocaleSwitcher properties.

type Props = {
  alternates: Alternates;
};

/**
 * LocaleSwitcher component.
 */

const LocaleSwitcher = (props: Props) => {
  const currentLocale = useLocale();
  const t = useTranslations();

  // TODO: Ensure Japanese locale is not shown until fully supported.
  const alternates = Object.fromEntries(
    Object.entries(props.alternates).filter(([locale]) => locale !== LOCALES.JA)
  );

  const localeLabels = {
    [LOCALES.EN]: t('locales.en.language'),
    [LOCALES.FR]: t('locales.fr.language'),
  };

  const chooseLanguageLabel = t('menu.chooseLanguage').trim();

  const renderedLinks = Object.entries(alternates).map(([locale, alternate]) => {
    const href = alternate === '' ? `/${locale}` : `/${locale}${alternate}`;
    const isActive = currentLocale === locale;
    const classes = clsx(styles.link, 'switch_lang_txt', isActive && styles.active);
    const localeLabel = localeLabels[locale as keyof typeof localeLabels] || locale.toUpperCase();
    const ariaLabel = `${chooseLanguageLabel}: ${localeLabel}`;

    return (
      <a
        href={href}
        className={classes}
        key={locale}
        aria-label={ariaLabel}
        hrefLang={locale}
        lang={locale}
      >
        <Text as="span" level="label-md" transform="uppercase" marginBottom="none">
          {locale}
        </Text>
      </a>
    );
  });

  return (
    <Flex gap="xxs" align="center" className={styles.localeSwitcher}>
      <Icon type="globe" className="nav_lang_icon" />
      {renderedLinks}
    </Flex>
  );
};

export default LocaleSwitcher;
